/*
Screen-reader announcements for dynamic changes that produce no focusable
result — row add/delete/copy, bulk paste/fill, table clears. ag-grid announces
the focused cell's value, but structural changes (a row appearing or vanishing)
are otherwise silent to assistive tech.

A single polite live region is lazily attached to <body> and shared by every
caller. Styles are inlined (standard visually-hidden pattern) rather than a
CSS class so the utility works anywhere without a stylesheet dependency.
*/

const REGION_ID = 'a11y-live-announcer';
// Long enough for a screen reader to pick the message up, short enough that a
// virtual-cursor user paging through the document later doesn't find stale
// "Row deleted" text hanging around.
const CLEAR_AFTER_MS = 7000;

let clearTimer = null;

function getRegion() {
  let region = document.getElementById(REGION_ID);
  if (region) return region;
  region = document.createElement('div');
  region.id = REGION_ID;
  region.setAttribute('role', 'status');
  region.setAttribute('aria-live', 'polite');
  region.setAttribute('aria-atomic', 'true');
  Object.assign(region.style, {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: '0',
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: '0',
  });
  document.body.appendChild(region);
  return region;
}

/**
 * Announce a message to screen-reader users via a shared polite live region.
 * Repeating the same message re-announces (the region is cleared first so the
 * text node genuinely changes).
 *
 * @param {string} message
 */
export default function announce(message) {
  if (!message || typeof document === 'undefined') return;
  const region = getRegion();
  region.textContent = '';
  // Next-tick write so consecutive identical messages register as a change.
  window.setTimeout(() => {
    region.textContent = String(message);
  }, 50);
  if (clearTimer) clearTimeout(clearTimer);
  clearTimer = setTimeout(() => {
    region.textContent = '';
    clearTimer = null;
  }, CLEAR_AFTER_MS);
}
